import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

import type { SearchParams } from './useRouterQuery';
import type { FilterShow } from '~/components/Filters2/types';
import { useAuthContext } from '~/auth/auth.context';
import { getStorageKey, readSavedDefaults } from './filterDefaultsStorage';

/**
 * URL keys a page may persist as the user's defaults: the Filters2 controls, the params behind a
 * composite control (the campaign page's Connection filter drives `connectionAccepted` and
 * `connectedUserId`), plus pages whose whole filter state lives in one param (RIA Search keeps
 * every filter in `riaFilters`, its sort in `riaSort`).
 */
export type PersistableQueryKey =
  | FilterShow
  | 'riaFilters'
  | 'riaSort'
  | 'connectionAccepted'
  | 'connectedUserId'
  | 'notConnectedUserId';

/**
 * Filter keys that should be persisted as user defaults.
 * Excludes pagination, temporary state, and path params.
 */
const PERSISTABLE_FILTER_KEYS: ReadonlySet<PersistableQueryKey> = new Set<PersistableQueryKey>([
  'userId',
  'cadenceUserId',
  'templateId',
  'order',
  'states',
  'response',
  'tag',
  'stageId',
  'stageId2',
  'companyId',
  'templateSort',
  'connectedUserId',
  'connectionAccepted',
  'notConnectedUserId',
  'connectionType',
  'riaFilters',
  'riaSort',
]);

/**
 * Checks if the URL has any filter params from the applicable filters.
 */
function hasAnyFilterParams(query: SearchParams, applicableFilters: PersistableQueryKey[]): boolean {
  return applicableFilters.some((filterKey) => {
    const value = query[filterKey as keyof SearchParams];
    return value !== undefined && value !== null;
  });
}

/**
 * Hook that manages persistent filter defaults per page.
 *
 * - Saves filter changes to localStorage automatically
 * - Applies saved defaults to URL on fresh navigation (no filter params)
 * - Respects shared URLs with explicit params
 */
export function useFilterDefaults(
  teamId: string,
  applicableFilters: PersistableQueryKey[],
): {
  saveDefaults: (query: SearchParams) => void;
  clearDefaults: () => void;
} {
  const router = useRouter();
  const [auth] = useAuthContext();
  const hasAppliedDefaults = useRef(false);
  // Tracks whether the current URL change was caused by filter interaction (saveDefaults)
  // vs navigation (clicking nav link). This distinguishes:
  // - Nav click: should apply defaults
  // - User clearing filters: should NOT apply defaults
  const isFilterInteraction = useRef(false);

  const impersonatedUserId = auth.impersonation?.userId ?? null;
  const storageKey = getStorageKey(teamId, router.pathname, impersonatedUserId);
  const persistableFilters = applicableFilters.filter((f) => PERSISTABLE_FILTER_KEYS.has(f));

  // Reset hasAppliedDefaults when asPath changes (SPA navigation or filter clear)
  // This ensures defaults are reapplied when clicking nav link while on same page
  useEffect(() => {
    if (isFilterInteraction.current) {
      // This URL change was from filter interaction, don't reset
      isFilterInteraction.current = false;
      return;
    }
    // Navigation occurred (nav link click), reset to allow defaults to be applied
    hasAppliedDefaults.current = false;
  }, [router.asPath]);

  // Apply saved defaults on mount (if URL has no filter params)
  useEffect(() => {
    // Only run once per mount, and only on client side
    if (hasAppliedDefaults.current || typeof window === 'undefined') return;

    // Wait for router to be ready (query params populated)
    if (!router.isReady) return;

    hasAppliedDefaults.current = true;

    // If URL already has filter params, respect them (shared URL case)
    if (hasAnyFilterParams(router.query as SearchParams, persistableFilters)) {
      return;
    }

    // Try to load saved defaults
    try {
      const saved = readSavedDefaults(localStorage, teamId, router.pathname, impersonatedUserId);
      if (!saved) return;

      const savedDefaults = JSON.parse(saved) as Partial<SearchParams>;
      if (Object.keys(savedDefaults).length === 0) return;

      // Filter to only applicable filters (in case saved data has stale keys)
      const applicableDefaults: Partial<SearchParams> = {};
      for (const key of persistableFilters) {
        if (key in savedDefaults && savedDefaults[key as keyof SearchParams] !== undefined) {
          applicableDefaults[key as keyof SearchParams] = savedDefaults[key as keyof SearchParams] as never;
        }
      }

      if (Object.keys(applicableDefaults).length === 0) return;

      // Apply defaults to URL via shallow replace
      void router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, ...applicableDefaults },
        },
        undefined,
        { shallow: true },
      );
    } catch {
      // Ignore localStorage errors (SSR, private browsing, etc.)
    }
  }, [router.isReady, router.pathname, router.query, teamId, impersonatedUserId, persistableFilters, router]);

  // Save filter changes to localStorage
  const saveDefaults = useCallback(
    (query: SearchParams) => {
      if (typeof window === 'undefined') return;

      // Mark as filter interaction so the asPath effect knows not to reset hasAppliedDefaults
      // This is called synchronously before the URL updates
      isFilterInteraction.current = true;

      try {
        // Extract only persistable filters that are set
        const defaults: Partial<SearchParams> = {};
        for (const key of persistableFilters) {
          const value = query[key as keyof SearchParams];
          if (value !== undefined && value !== null) {
            defaults[key as keyof SearchParams] = value as never;
          }
        }

        localStorage.setItem(storageKey, JSON.stringify(defaults));
      } catch {
        // Ignore localStorage errors
      }
    },
    [storageKey, persistableFilters],
  );

  // Clear saved defaults (used when user clicks "Clear")
  const clearDefaults = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore localStorage errors
    }
  }, [storageKey]);

  return { saveDefaults, clearDefaults };
}

/** This page's saved-defaults key, for {@link forgetSavedFilters}. */
export function useFilterDefaultsKey(teamId: string): string {
  const router = useRouter();
  const [auth] = useAuthContext();
  return getStorageKey(teamId, router.pathname, auth.impersonation?.userId ?? null);
}
